import { EventEmitter } from 'events';
import { merge } from 'lodash';
import {
  RequestServiceConfigurations,
  APIDirectoryType,
  RequestSettings,
  NetworkResponse,
  API,
  APIOnlyRequestSettings,
} from '../types/services/request.types';
import {
  DEFAULT_API_VERSION_FORMAT,
  DEFAULT_CONFIGURATIONS,
  REQUEST_ERROR_TYPES,
} from '../constants/services/request.constants';
import { TIMER_EVENTS } from '../constants/timers.constants';
import { waitUntil } from '../timers';
import { RequestError } from '../errors/request.error';
import { RESPONSE_TYPES } from '../constants/http-response.constants';

export class RequestService extends EventEmitter {
  private APIDirectory: APIDirectoryType = {};
  private maxRetries = 0;
  private defaultContentType = '';

  constructor(configurations?: RequestServiceConfigurations) {
    super();
    const mergedConfigurations = Object.assign({}, DEFAULT_CONFIGURATIONS, configurations);

    this.maxRetries = mergedConfigurations.maxRetries;
    this.defaultContentType = mergedConfigurations.defaultContentType;
    mergedConfigurations.APIConfigList.forEach((configuration) => this.setUpAPI(configuration));
  }

  setUpAPI(apiConfiguration: API) {
    const headers = apiConfiguration.standardSettings?.headers;
    const { version } = apiConfiguration;

    this.APIDirectory[apiConfiguration.name] = {
      name: apiConfiguration.name,
      host: apiConfiguration.host,
      version: apiConfiguration.version,
      apiKey: apiConfiguration.apiKey,
      requestTimeout: apiConfiguration.requestTimeout,
      maxRetries: apiConfiguration.maxRetries,
      standardSettings: {
        ...(apiConfiguration.standardSettings || {}),
        headers: {
          ...(headers || {}),
          'Content-Type': `${headers?.['Content-Type'] || this.defaultContentType}${version ? (DEFAULT_API_VERSION_FORMAT + version) : ''}`,
        },
      },
    };
  }

  private processResponse<T>(response: Response, type: RESPONSE_TYPES): Promise<T> {
    return response[type]();
  }

  private setAPISettingsFromWindow(targetApi: string, defaultApi: string): void {
    const apiList = window.pesConfigs?.API?.list;
    const targetFound = apiList?.find(({ name }) => targetApi === name);

    if (targetFound) {
      this.setUpAPI(targetFound);
      return;
    }

    if (this.APIDirectory[defaultApi]) return;

    const defaultFound = apiList?.find(({ name }) => defaultApi === name);

    if (defaultFound) {
      this.setUpAPI(defaultFound);
      return;
    }

    throw new RequestError(REQUEST_ERROR_TYPES.SET_API_ERROR);
  }

  private getFetchSettingsByAPI(
    settings: APIOnlyRequestSettings,
    abortSignal: AbortSignal,
  ): RequestSettings {
    if (!(settings.api.target in this.APIDirectory)) {
      this.setAPISettingsFromWindow(
        settings.api.target as string,
        settings.api.default as string,
      );
    }

    const foundAPIName: keyof APIDirectoryType = settings.api.target in this.APIDirectory
      ? settings.api.target
      : settings.api.default;
    const foundAPI = this.APIDirectory[foundAPIName];
    const copiedSettings = { ...settings };

    if (settings.standardSettings?.headers?.['Content-Type'] && foundAPI.version) {
      const contentType = copiedSettings.standardSettings.headers['Content-Type'];
      const APIVersion = foundAPI.version;

      copiedSettings.standardSettings.headers = {
        ...settings.standardSettings.headers,
        'Content-Type': contentType + (APIVersion ? (DEFAULT_API_VERSION_FORMAT + APIVersion) : ''),
      };
    }
    const mergedStandardSettings = merge(
      {},
      copiedSettings.standardSettings,
      this.APIDirectory[foundAPIName].standardSettings,
    );

    return {
      ...settings,
      host: foundAPI.host || settings.host,
      timeout: foundAPI.requestTimeout ?? settings.timeout,
      maxRetries: foundAPI.maxRetries ?? settings.maxRetries,
      standardSettings: {
        ...mergedStandardSettings,
        signal: abortSignal,
      },
    };
  }

  get<T>(url: string, settings?: RequestSettings): NetworkResponse<T> {
    const fullSettings: RequestSettings = {
      ...settings,
      standardSettings: {
        ...settings?.standardSettings,
        method: 'GET',
      },
    };

    return this.request(url, fullSettings, 0);
  }

  post<T>(url: string, body: any, settings?: RequestSettings): NetworkResponse<T> {
    const fullSettings: RequestSettings = {
      ...settings,
      standardSettings: {
        ...settings?.standardSettings,
        body,
        method: 'POST',
      },
    };

    return this.request(url, fullSettings, 0);
  }

  patch<T>(url: string, body: any, settings?: RequestSettings): NetworkResponse<T> {
    const fullSettings: RequestSettings = {
      ...settings,
      standardSettings: {
        ...settings?.standardSettings,
        body,
        method: 'PATCH',
      },
    };

    return this.request(url, fullSettings, 0);
  }

  put<T>(url: string, body: any, settings?: RequestSettings): NetworkResponse<T> {
    const fullSettings: RequestSettings = {
      ...settings,
      standardSettings: {
        ...settings?.standardSettings,
        body,
        method: 'PUT',
      },
    };

    return this.request(url, fullSettings, 0);
  }

  delete<T>(url: string, settings?: RequestSettings): NetworkResponse<T> {
    const fullSettings: RequestSettings = {
      ...settings,
      standardSettings: {
        ...settings?.standardSettings,
        method: 'DELETE',
      },
    };

    return this.request(url, fullSettings, 0);
  }

  async request<T>(url: string, settings?: RequestSettings, retries = 0): NetworkResponse<T> {
    let APIMaxRetries = 0;
    try {
      if (!settings) {
        const response = await (await fetch(url)).json();
        return { error: null, result: response };
      }

      const abortController = new AbortController();
      const timeoutStopper = new EventEmitter();
      let fetchSettings: RequestSettings = null;

      if (settings.api) {
        fetchSettings = {
          ...this.getFetchSettingsByAPI(
            settings as APIOnlyRequestSettings,
            abortController.signal,
          ),
        };

        APIMaxRetries = fetchSettings.maxRetries;
      }

      const processedSettings = fetchSettings || settings;

      if (processedSettings.timeout) {
        waitUntil(processedSettings.timeout, timeoutStopper).then((requestDidTimeout) => {
          if (!requestDidTimeout) return;

          abortController.abort();
        });
      }

      let finalUrl = url;

      if (processedSettings.host && !url.startsWith('/')) {
        const urlObject = new URL(url);
        finalUrl = url.replace(urlObject.origin, '');
      }

      const response = await fetch(`${processedSettings.host ?? ''}${finalUrl}`, processedSettings.standardSettings);
      timeoutStopper.emit(TIMER_EVENTS.STOP);

      if (!response.ok) {
        throw new RequestError(response.status);
      }

      const processedResponse: T = await this.processResponse<T>(
        response,
        processedSettings.responseType ?? RESPONSE_TYPES.JSON,
      );
      return { error: null, result: processedResponse };
    } catch (error) {
      const maxRetries = settings?.maxRetries || APIMaxRetries || this.maxRetries || 0;

      const isAPISetupError = error instanceof RequestError
        && error.type === REQUEST_ERROR_TYPES.SET_API_ERROR;

      if (!isAPISetupError && maxRetries > 0 && retries < maxRetries) {
        return this.request(url, settings, retries + 1);
      }

      return { error: error as Error, result: null };
    }
  }
}

const instance = new RequestService();
export default instance;
export const RequestServiceInstance = instance;
